/**
 * Type guards for Player API (BEWOTEC SOAP)
 *
 * All type guards are now powered by Zod schemas for automatic runtime validation.
 * This ensures type guards stay in sync with schemas and provide actual validation.
 *
 * Benefits:
 * - Type guards automatically stay in sync with schemas
 * - Actual runtime validation of data structure
 * - Much less code to maintain
 * - Better error messages from Zod
 *
 * @example
 * const result = parsed?.Envelope?.Body?.GetProductListResponse?.GetProductListResult;
 *
 * if (isGetProductListResult(result)) {
 *   // TypeScript knows result is GetProductListResult
 *   // AND the data has been validated at runtime
 *   const products = result.Products?.Product || [];
 * }
 */

import {
  GetProductListResultSchema,
  GetProductOffersResultSchema,
  GetAvailableValuesResultSchema,
  type GetProductListResult,
  type GetProductOffersResult,
  type GetAvailableValuesResult,
} from './schemas';

/**
 * Type guard for GetProductListResult
 *
 * Uses Zod schema validation for runtime type checking.
 *
 * @param value - Unknown value to check
 * @returns True if value is a valid GetProductListResult
 */
export function isGetProductListResult(value: unknown): value is GetProductListResult {
  return GetProductListResultSchema.safeParse(value).success;
}

/**
 * Type guard for GetProductOffersResult
 *
 * Uses Zod schema validation for runtime type checking.
 *
 * @param value - Unknown value to check
 * @returns True if value is a valid GetProductOffersResult
 */
export function isGetProductOffersResult(value: unknown): value is GetProductOffersResult {
  return GetProductOffersResultSchema.safeParse(value).success;
}

/**
 * Type guard for GetAvailableValuesResult
 *
 * Uses Zod schema validation for runtime type checking.
 *
 * @param value - Unknown value to check
 * @returns True if value is a valid GetAvailableValuesResult
 */
export function isGetAvailableValuesResult(value: unknown): value is GetAvailableValuesResult {
  return GetAvailableValuesResultSchema.safeParse(value).success;
}

/**
 * Type guard for SOAP envelope response
 *
 * Checks if value has the standard SOAP envelope structure.
 *
 * @param value - Unknown value to check
 * @returns True if value is a SOAP envelope
 */
export function isSoapEnvelope(value: unknown): value is {
  Envelope: {
    Body: Record<string, unknown>;
  };
} {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;
  const envelope = record.Envelope;

  if (typeof envelope !== 'object' || envelope === null) {
    return false;
  }

  const envelopeRecord = envelope as Record<string, unknown>;
  const body = envelopeRecord.Body;

  return typeof body === 'object' && body !== null;
}

/**
 * Type guard for non-empty arrays
 *
 * Useful for checking if an array has items before processing.
 *
 * @param value - Value to check
 * @returns True if value is a non-empty array
 */
export function isNonEmptyArray<T>(value: T[] | undefined | null): value is T[] {
  return Array.isArray(value) && value.length > 0;
}
